import { ReactNode, useRef, useState } from 'react'
import { ElementOverrides, Template } from '@/models/drawing/drawingTypes.ts'
import { domToJpeg } from 'modern-screenshot'
import { Button } from '@/components/Button.tsx'
import DrawingCanvas, { DrawingCanvasHandle } from './DrawingCanvas.tsx'
import { shareDownloadURL } from '@/controller/updateHelper.ts'
import { useTranslation } from 'react-i18next'
import useDrawingTemplates from '@/hooks/useDrawingTemplates.tsx'

const DIMENSION_A5 = {
  width: 1748,
  height: 2480,
}

export interface DrawingEditorToolsConfig {
  hideDownload?: boolean
  toolButtons: Array<DrawingEditorToolButton>
}

export interface DrawingEditorToolButton {
  onGetDataUrl?: (data: string) => Promise<void>
  // setIsGettingData?: (isGettingData: boolean) => void
  element: ReactNode
}

export default function DrawingEditor({ template: defaultTemplate, elementOverrides, toolsConfig }: {template?: Template, elementOverrides?: ElementOverrides, toolsConfig?: DrawingEditorToolsConfig}) {
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<DrawingCanvasHandle>(null)
  const drawingTemplates = useDrawingTemplates()
  const [template] = useState<Template>(defaultTemplate || drawingTemplates[2])
  const [isDownloading, setIsDownloading] = useState(false)
  const { t } = useTranslation()


  async function exportToDataUrl(): Promise<string> {
    if (!canvasContainerRef.current || !canvasRef.current) throw new Error('Canvas not found')

    try {
      // Store current dimensions and container styles
      const originalDimensions = canvasRef.current.getDimensions()
      const container = canvasContainerRef.current
      const originalContainerStyles = {
        minWidth: container.style.minWidth,
        minHeight: container.style.minHeight,
        overflow: container.style.overflow,
      }

      // Make container large enough to fit A5 card
      container.style.minWidth = `${DIMENSION_A5.width}px`
      container.style.minHeight = `${DIMENSION_A5.height}px`
      container.style.overflow = 'visible'

      // Now set card to A5 dimensions
      canvasRef.current.setDimensions({ width: DIMENSION_A5.width, height: DIMENSION_A5.height })

      // Wait for re-render
      await new Promise(resolve => setTimeout(resolve, 300))

      // Get the card element from the canvas container
      const cardElement = container.querySelector('.relative.shadow-lg') as HTMLElement
      if (!cardElement) {
        canvasRef.current.setDimensions(originalDimensions)
        throw new Error('Canvas element not found')
      }

      // Verify the card has actually resized to A5 dimensions
      const cardWidth = cardElement.offsetWidth
      const cardHeight = cardElement.offsetHeight
      console.log('Card size before capture:', cardWidth, 'x', cardHeight)
      console.log('Target size:', DIMENSION_A5.width, 'x', DIMENSION_A5.height)
      await new Promise(resolve => setTimeout(resolve, 300))

      // Capture the card with consistent pixel ratio
      const dataUrl = await domToJpeg(cardElement, {
        quality: 0.9,
        backgroundColor: 'white',
      })

      // Restore everything
      canvasRef.current.setDimensions(originalDimensions)
      container.style.minWidth = originalContainerStyles.minWidth
      container.style.minHeight = originalContainerStyles.minHeight
      container.style.overflow = originalContainerStyles.overflow

      return dataUrl

    } catch (error) {
      console.error('Failed to create data URL image:', error)
      throw error
    }
  }

  // Handle download
  const handleDownload = async () => {
    if (!canvasContainerRef.current || !canvasRef.current) return

    try {
      setIsDownloading(true)
      const dataUrl = await exportToDataUrl()

      // Save the file
      shareDownloadURL(dataUrl, 'fridaysnap-card.jpg', setIsDownloading)
    } catch (error) {
      console.error('Failed to download image:', error)
      setIsDownloading(false)
    }
  }


  return (
    <div className='flex w-full h-full flex-col'>
      <div className='flex flex-col justify-center items-center'>
        {
          !toolsConfig?.hideDownload && <Button className='mt-3' color='primary' onClick={handleDownload} disabled={isDownloading} loading={isDownloading}>
            {t('common.download')}
          </Button>
        }
        { toolsConfig?.toolButtons.map((tool, i) => <div key={i} onClick={tool.onGetDataUrl ? () => exportToDataUrl().then(tool.onGetDataUrl) : undefined}>
          {tool.element}
        </div>)}
      </div>
      <div className="flex-1 flex flex-col w-full h-full">
        <div className="w-full h-full" ref={canvasContainerRef}>
          <DrawingCanvas ref={canvasRef} template={template} elementOverrides={elementOverrides} />
        </div>
      </div>
    </div>
  )
}
